'use client';

import {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import { UserContext } from '../../UserProvider';
import {
  getAllUsers as getAllUsersV2,
  getUserCompanies,
} from '@/_api/v2/admin-users';
import {
  getCompanyByHashId,
  getCompanyTaxReturns,
  getCompanySignatureRequests,
  getCompanyQuestionnaires,
  getAllExportableDocumentsForEntity,
} from '@/_api/admin-companies';
import { getHash } from '@/_utils/companies';
import { useParams } from 'next/navigation';
import { Company } from '@/_types/models/company';
import { User } from '@/_types/models/user';
import {
  DEFAULT_CLIENT_COLUMNS,
  DEFAULT_CLIENT_FILTER,
  DEFAULT_V2_USERS_FILTER,
} from '@/_constants/clients';
import { USERS_PAGE_SIZE_COOKIE } from '@/_constants/cookies';
import { setCookie, getCookie } from 'cookies-next/client';
import { areSameObjects } from '@/_utils/objects';
import { TaxReturn } from '@/_types/models/taxReturns';
import { SignatureRequest } from '@/_types/models/signatureRequests';
import { ClientTagsDialog } from './_components/ClientsTagsDialog';
import { ClientsAddDialog } from './_components/ClientsAddDialog';
import type { CompanyProviderProps } from './CompanyProvider.types';
import type { QuestionnaireIndexSerializer } from '@/_types/serializers/v1/questionnaires';
import type {
  ClientsColumnType,
  ClientsFilterObject,
  UsersV2FilterObject,
} from '@/_types/clients';
import { EMPTY_TAG_STRING } from '@/_constants/firm';
import { UserEditDialog } from './_components/UserEditDialog';
import { ClientsImportDialog } from './_components/ClientsImportDialog';
import { getCompany } from '@/_api/client-companies';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { SortingState } from '@tanstack/react-table';
import { Conversation } from '@/_types/models/conversations';
import { getCompanyConversations } from '@/_api/admin-conversations';
import { getRelatedEntities, getAdminEntities } from '@/_api/v2/admin-entities';
import { DEFAULT_V2_ENTITY_FILTER } from '@/_constants/entities';
import { DropLinkUpload } from '@/_types/models/dropLink';
import { CURRENT_TAX_YEAR, TAX_YEARS } from '@/_constants/year-options';
import type { ExportableDocument } from '@/_contexts/ReviewAndExportProvider/ReviewAndExportProvider.types';

const CompanyContext = createContext<CompanyProviderProps>({
  addOrUpdateUser: (_user: User) => null,
  allUserIds: [],
  columns: DEFAULT_CLIENT_COLUMNS,
  entityTypeCounts: {},
  exportableDocs: [],
  filter: DEFAULT_CLIENT_FILTER,
  filteredClients: [],
  isCompanyLoading: true,
  isConversationsLoading: false,
  isExportableDocsLoading: false,
  isFiltering: false,
  isLoading: true,
  isQuestionnairesLoading: true,
  isSelectedCompanySubscribedToNotifications: false,
  isSignatureRequestsLoading: true,
  isTaxReturnsLoading: true,
  loadEntityTypeCounts: () => Promise.resolve(),
  loadUsersForTable: () => Promise.resolve(),
  newClientEntityType: undefined,
  onClientFilterChange: () => null,
  onToggleAddClient: () => null,
  onToggleClientTags: () => null,
  onToggleEditUser: () => null,
  onToggleImportClients: () => null,
  pagination: { pageIndex: 0, pageSize: 10 },
  refreshCompanyConversations: () => null,
  refreshCompanyDetails: () => null,
  refreshCompanyQuestionnaires: () => null,
  refreshCompanySignatureRequests: () => null,
  refreshCompanyTaxReturns: () => null,
  refreshExportableDocs: () => Promise.resolve(),
  removeUser: (_userId?: number) => null,
  resetFilters: () => null,
  selectedClient: undefined,
  selectedCompany: undefined,
  selectedCompanyDetails: undefined,
  selectedCompanyConversations: [],
  selectedCompanyInternalNotes: 0,
  selectedCompanyQuestionnaires: [],
  selectedCompanyRelatedCompanies: [],
  selectedCompanySignatureRequests: [],
  selectedCompanyTags: [EMPTY_TAG_STRING],
  selectedCompanyTaxReturns: [],
  selectedHash: undefined,
  selectedTaxReturn: undefined,
  selectedUser: undefined,
  selectedUserCompanies: [],
  selectedUserPrimaryCompanies: [],
  setFilter: () => null,
  setIsSorted: () => null,
  setNewClientEntityType: () => null,
  setPagination: () => null,
  setSelectedTaxReturn: () => null,
  setSelectedUser: () => null,
  setShowAddClientsImport: () => null,
  setSorting: () => null,
  sorting: [],
  updateSelectedCompany: () => null,
  usersCount: 0,
  usersForTable: [],
  dropLinkUpload: null,
  setDropLinkUpload: () => null,
  hasAnyExportableDocs: false,
  mostRecentYearWithDocs: null,
});

const CompanyProvider: any = ({ children }: any) => {
  const { isLoadingFirmPreferences, firmEntityTypes } =
    useContext(FirmSettingsContext);
  const { user, token, isAdvisor } = useContext(UserContext);
  const [isCompanyLoading, setCompanyLoading] = useState<boolean>(true);
  const [isEditingUser, setEditingUser] = useState<boolean>(false);
  const [showAddClientsImportDialog, setShowAddClientsImport] =
    useState<boolean>(false);
  const [newClientEntityType, setNewClientEntityType] = useState<string>('');
  const [showAddClient, setShowAddClient] = useState<boolean>(false);
  const [showClientTags, setShowClientTags] = useState<boolean>(false);
  const [selectedTaxReturn, setSelectedTaxReturn] = useState<
    TaxReturn | undefined
  >(undefined);
  const [isSorted, setIsSorted] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedUserCompanies, setSelectedUserCompanies] = useState<
    Array<Company>
  >([]);
  const [entityTypeCounts, setEntityTypeCounts] = useState<
    Record<string, number>
  >({});
  const [dropLinkUpload, setDropLinkUpload] = useState<DropLinkUpload | null>(
    null,
  );
  const [exportableDocs, setExportableDocs] = useState<ExportableDocument[]>(
    [],
  );
  const [isExportableDocsLoading, setIsExportableDocsLoading] = useState(false);
  const [hasAnyExportableDocs, setHasAnyExportableDocs] =
    useState<boolean>(false);
  const [mostRecentYearWithDocs, setMostRecentYearWithDocs] = useState<
    number | null
  >(null);

  const onToggleImportClients = () => {
    setShowAddClientsImport(!showAddClientsImportDialog);
  };

  const onCloseClientImportDialog = () => {
    setShowAddClientsImport(false);
  };

  const onToggleAddClient = () => setShowAddClient(!showAddClient);
  const onToggleClientTags = () => setShowClientTags(!showClientTags);
  const onToggleEditUser = () => {
    if (isEditingUser) {
      setSelectedUser(undefined);
      setSelectedUserCompanies([]);
    }

    setEditingUser(!isEditingUser);
  };

  const [filter, setFilter] = useState<any>(DEFAULT_V2_USERS_FILTER);
  const [columns, _setColumns] = useState<Array<ClientsColumnType>>(
    DEFAULT_CLIENT_COLUMNS,
  );
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isConversationsLoading, setConversationsLoading] =
    useState<boolean>(true);
  const [isQuestionnairesLoading, setQuestionnairesLoading] =
    useState<boolean>(true);
  const [isSignatureRequestsLoading, setSignatureRequestsLoading] =
    useState<boolean>(true);
  const [isTaxReturnsLoading, setTaxReturnsLoading] = useState<boolean>(true);

  const [allUserIds, setAllUserIds] = useState<number[]>([]);
  const [users, setUsers] = useState<Array<User>>([]);
  const [usersCount, setUsersCount] = useState<number>(0);
  const [selectedCompanyRelatedCompanies, setSelectedCompanyRelatedCompanies] =
    useState<Array<Company>>([]);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>(
    undefined,
  );
  const [selectedCompanyDetails, setSelectedCompanyDetails] = useState<
    Company | undefined
  >(undefined);

  const [selectedCompanyConversations, setSelectedCompanyConversations] =
    useState<Array<Conversation>>([]);

  const { companyHash, userId } = useParams();

  const selectedClient: User | undefined = useMemo(() => {
    const c: User | undefined = users?.find(
      (client) => client?.id?.toString() === userId,
    );

    return c;
  }, [users, userId]);

  const updateSelectedCompany = (company: Partial<Company>) => {
    if (!company?.id) return;

    if (typeof selectedCompany?.id === 'number') {
      const updatedCompany: Company = { ...selectedCompany, ...company };
      setSelectedCompany(updatedCompany);
    }
  };

  const selectedHash: string | undefined = getHash(companyHash?.toString?.());

  const loadCompanyDetails = useCallback(
    async (companyId?: number) => {
      const localCompanyId: number | undefined =
        companyId || selectedCompany?.id;

      if (typeof localCompanyId !== 'number') return;

      try {
        const companyDetails = await getCompany({
          companyId: localCompanyId!,
          token,
        });

        setSelectedCompanyDetails(companyDetails);
        setSelectedCompany(companyDetails);
      } catch (error) {
        console.error(error);
      }
    },
    [token, selectedCompany?.id],
  );

  const selectedCompanyTags = useMemo(() => {
    return selectedCompanyDetails?.entity_tags?.map((tag) => tag.name) || [];
  }, [selectedCompanyDetails]);

  const selectedCompanyInternalNotes: number = useMemo(
    () => selectedCompanyDetails?.note_count ?? 0,
    [selectedCompanyDetails],
  );

  const isSelectedCompanySubscribedToNotifications: boolean = useMemo(
    () =>
      Boolean(
        selectedCompanyDetails?.notification_subscribed ||
          (user?.id &&
            selectedCompanyDetails?.notification_subscribed_users?.includes(
              user?.id,
            )),
      ),
    [selectedCompanyDetails, user?.id],
  );

  const [selectedCompanyQuestionnaires, setSelectedCompanyQuestionnaires] =
    useState<Array<QuestionnaireIndexSerializer>>([]);
  const [
    selectedCompanySignatureRequests,
    setSelectedCompanySignatureRequests,
  ] = useState<Array<SignatureRequest>>([]);
  const [selectedCompanyTaxReturns, setSelectedCompanyTaxReturns] = useState<
    Array<TaxReturn>
  >([]);

  const loadCompanyQuestionnaires = useCallback(
    async (companyId?: number, isSilent?: boolean) => {
      const localCompanyId: number | undefined =
        companyId || selectedCompany?.id;

      if (typeof localCompanyId !== 'number') return;

      if (!isSilent) setQuestionnairesLoading(true);

      const questionnaires = await getCompanyQuestionnaires({
        companyId: localCompanyId!,
        token,
      });

      const orderedQuestionnaires = questionnaires.sort(
        (a, b) =>
          new Date(b.last_activity || '').getTime() -
          new Date(a.last_activity || '').getTime(),
      );

      setSelectedCompanyQuestionnaires(orderedQuestionnaires);
      setQuestionnairesLoading(false);
    },
    [selectedCompany?.id, token],
  );

  const loadCompanyConversations = useCallback(
    async (companyId?: number, isSilent?: boolean) => {
      const localCompanyId: number | undefined =
        companyId || selectedCompany?.id;

      if (typeof localCompanyId !== 'number') return;

      try {
        if (!isSilent) setConversationsLoading(true);

        const companyConversations = await getCompanyConversations({
          companyId: localCompanyId!,
          token,
        });

        // handle error object case
        if (!Array.isArray(companyConversations)) {
          setSelectedCompanyConversations([]);
          return;
        }

        setSelectedCompanyConversations(companyConversations);
      } catch (error) {
        console.error(error);
      } finally {
        setConversationsLoading(false);
      }
    },
    [selectedCompany?.id, token],
  );

  const loadCompanyRelatedCompanies = useCallback(
    async (companyId?: number) => {
      if (!token) return;

      const localCompanyId: number | undefined =
        companyId || selectedCompany?.id;

      if (typeof localCompanyId !== 'number') return;

      try {
        const relatedCompanies = await getRelatedEntities({
          entityId: localCompanyId!,
          token,
        });

        if (relatedCompanies?.error || !Array.isArray(relatedCompanies)) {
          throw new Error('failed to load related companies');
        }

        setSelectedCompanyRelatedCompanies(relatedCompanies);
      } catch (error) {
        console.error(error);
      }
    },
    [selectedCompany?.id, token],
  );

  const loadCompanySignatureRequests = useCallback(
    async (companyId?: number) => {
      const localCompanyId: number | undefined =
        companyId || selectedCompany?.id;

      if (typeof localCompanyId !== 'number') return;

      try {
        setSignatureRequestsLoading(true);

        const signatureRequests = (await getCompanySignatureRequests({
          companyId: localCompanyId,
          token,
        })) as Array<SignatureRequest>;

        setSelectedCompanySignatureRequests(signatureRequests);
      } finally {
        setSignatureRequestsLoading(false);
      }
    },
    [selectedCompany?.id, token],
  );

  const selectedUserPrimaryCompanies: Array<Company> = useMemo(
    () =>
      selectedUserCompanies?.filter(
        (company) => company.user?.id === selectedUser?.id,
      ) || [],
    [selectedUser?.id, selectedUserCompanies],
  );

  const loadCompanyTaxReturns = useCallback(
    async (companyId?: number) => {
      const localCompanyId: number | undefined =
        companyId || selectedCompany?.id;

      if (typeof localCompanyId !== 'number') return;

      setTaxReturnsLoading(true);

      const taxReturns = (await getCompanyTaxReturns({
        companyId: localCompanyId,
        token,
      })) as Array<TaxReturn>;

      setTaxReturnsLoading(false);
      setSelectedCompanyTaxReturns(taxReturns);
    },
    [selectedCompany?.id, token],
  );

  const loadExportableDocs = useCallback(
    async (companyId?: number, company?: Company) => {
      if (!companyId || !token) {
        setExportableDocs([]);
        setIsExportableDocsLoading(false);
        setHasAnyExportableDocs(false);
        setMostRecentYearWithDocs(null);
        return;
      }

      // Non-individual entities never have exportable docs
      if (company?.entity_type !== 'Individual') {
        setExportableDocs([]);
        setIsExportableDocsLoading(false);
        setHasAnyExportableDocs(false);
        setMostRecentYearWithDocs(null);
        return;
      }

      try {
        setIsExportableDocsLoading(true);

        // Check all years in parallel to find docs and most recent year
        const allYears = TAX_YEARS.map((year) => parseInt(year.value));

        const promises = allYears.map(async (year) => {
          try {
            const result = await getAllExportableDocumentsForEntity({
              id: companyId,
              year,
              token,
            });
            const docs = Array.isArray(result)
              ? result
              : (result as { data: ExportableDocument[] }).data;
            return { year, docs: docs || [] };
          } catch (error) {
            return { year, docs: [] };
          }
        });

        const results = await Promise.all(promises);

        // Set current year docs (for backward compatibility)
        const currentYearResult = results.find(
          (r) => r.year === parseInt(CURRENT_TAX_YEAR),
        );
        setExportableDocs(currentYearResult?.docs || []);

        // Check if any year has docs and find most recent year
        const yearsWithDocs = results.filter(
          (result) => result.docs.length > 0,
        );

        if (yearsWithDocs.length === 0) {
          setHasAnyExportableDocs(false);
          setMostRecentYearWithDocs(null);
        } else {
          setHasAnyExportableDocs(true);
          const mostRecentYear = Math.max(
            ...yearsWithDocs.map((result) => result.year),
          );
          setMostRecentYearWithDocs(mostRecentYear);
        }
      } catch (error) {
        console.error('Error fetching exportable documents:', error);
        setExportableDocs([]);
        setHasAnyExportableDocs(false);
        setMostRecentYearWithDocs(null);
      } finally {
        setIsExportableDocsLoading(false);
      }
    },
    [token],
  );

  const _loadUsersForTable = useCallback(async () => {
    if (
      isLoadingFirmPreferences ||
      !isAdvisor ||
      !token ||
      typeof user?.id !== 'number'
    ) {
      return;
    }

    try {
      setLoading(true);

      const params: UsersV2FilterObject = {
        token,
        ...DEFAULT_V2_USERS_FILTER,
        ...filter,
        page: pagination?.pageIndex ?? 0,
        rows_per_page: pagination?.pageSize,
        sort_by: sorting?.[0]?.id || 'created_at',
        descending: !Boolean(sorting?.[0]?.desc),
      };

      if (user?.firm_access === 'group' && user?.firm_group?.id) {
        params.firm_group_id = user.firm_group.id;
      }

      const { count } = await getAllUsersV2({
        ...params,
        count: true,
      });

      setUsersCount(count || 0);

      const { ids } = await getAllUsersV2({
        ...params,
        only_ids: true,
      });

      setAllUserIds(ids);

      const { users } = await getAllUsersV2(params);

      const cleanUsers: Array<any> = Array.from(
        new Map(
          users.map((user: any) => [
            user.id,
            { ...user, full_name: `${user.fname} ${user.lname}` } as User,
          ]),
        ).values(),
      );

      setUsers(cleanUsers);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [
    filter,
    isAdvisor,
    isLoadingFirmPreferences,
    pagination?.pageIndex,
    pagination?.pageSize,
    sorting,
    token,
    user?.firm_access,
    user?.firm_group?.id,
    user?.id,
  ]);

  const resetCompanyState = () => {
    setSelectedCompanyQuestionnaires([]);
    setSelectedCompanySignatureRequests([]);
    setSelectedCompanyTaxReturns([]);
    setSelectedCompanyRelatedCompanies([]);
    setExportableDocs([]);
    setHasAnyExportableDocs(false);
    setMostRecentYearWithDocs(null);
  };

  const getCompanyData = useCallback(
    async (company?: Company) => {
      if (!isAdvisor) return;
      if (!company?.id) {
        return resetCompanyState();
      }

      await Promise.all([
        loadCompanyQuestionnaires(company?.id, false),
        loadCompanySignatureRequests(company?.id!),
        loadCompanyTaxReturns(company?.id!),
        loadCompanyDetails(company?.id!),
        loadCompanyConversations(company?.id, false),
        loadCompanyRelatedCompanies(company?.id),
        loadExportableDocs(company?.id, company),
      ]);
    },
    [
      loadCompanyDetails,
      loadCompanyRelatedCompanies,
      loadCompanyConversations,
      loadCompanyQuestionnaires,
      loadCompanySignatureRequests,
      loadCompanyTaxReturns,
      loadExportableDocs,
      isAdvisor,
    ],
  );

  const sortedUsers: Array<User> = useMemo(() => {
    if (isSorted) {
      return users?.filter((u) => !u.is_advisor);
    }

    const usersWithTaxReturnName = users?.filter((u) => u.tax_return_name);

    // Doing this check because if tax_return_name is missing on almost all the users, it would be better to just retain the old order.
    if (usersWithTaxReturnName.length < users.length - 1) {
      return users?.filter((u) => !u.is_advisor);
    }

    return (
      users
        ?.filter((u) => !u.is_advisor)
        .sort((a, b) => {
          const aName = a.tax_return_name || '';
          const bName = b.tax_return_name || '';
          return aName.localeCompare?.(bName);
        }) || []
    );
  }, [isSorted, users]);

  const filteredClients: Array<Partial<User>> = useMemo(() => {
    let newClients: Array<Partial<User>> = [...sortedUsers].sort((a, b) => {
      const dateA: Date = new Date(a.created_at ?? 0);
      const dateB: Date = new Date(b.created_at ?? 0);

      return dateB.getTime() - dateA.getTime();
    });

    if (!!filter?.search_term) {
      newClients = newClients?.filter((user: Partial<User>) => {
        const lowerCaseSearch: string = filter?.search_term
          ?.toLowerCase()
          .replace(/,/g, '')
          .trim();

        return (
          `${user.lname?.toLowerCase()} ${user.fname?.toLowerCase()}`.includes(
            lowerCaseSearch,
          ) ||
          user.tax_return_name?.toLowerCase().includes(lowerCaseSearch) ||
          user.full_name?.toLowerCase().includes(lowerCaseSearch) ||
          user.work_email?.toLowerCase().includes(lowerCaseSearch) ||
          user.phone_number?.toLowerCase().includes(lowerCaseSearch) ||
          user.preferred_name?.toLowerCase().includes(lowerCaseSearch) ||
          user.email?.toLowerCase().includes(lowerCaseSearch) ||
          user.external_user_id?.toLowerCase().includes(lowerCaseSearch)
        );
      });
    }

    if (filter?.assigned_advisor_id !== -1) {
      newClients = newClients?.filter(
        (c) => c.assigned_advisor_id === filter?.assigned_advisor_id,
      );
    }

    if (filter?.firm_group_id !== -1) {
      newClients = newClients?.filter(
        (c) => filter?.firm_group_id === c.firm_group?.id,
      );
    }

    if (filter?.selected_client_tags_filter?.length) {
      if (filter?.selected_client_tags_filter.includes(EMPTY_TAG_STRING)) {
        newClients = newClients?.filter(
          (client) => !client.contact_tags?.length,
        );
      } else {
        newClients = newClients?.filter((client) =>
          filter?.selected_client_tags_filter.every((clientTag: string) =>
            client.contact_tags?.map((ct) => ct.name).includes(clientTag),
          ),
        );
      }
    }

    if (filter?.hide_inactives) {
      newClients = newClients?.filter((c) => c.paying);
    }

    if (filter?.hide_actives) {
      newClients = newClients?.filter((c) => c.paying === false);
    }

    if (filter?.show_only_deleted) {
      newClients = newClients?.filter((c) => c.deleted_at);
    }

    return newClients;
  }, [sortedUsers, filter]);

  const loadCompany = useCallback(async () => {
    if (!token || !isAdvisor || !selectedHash) return;

    try {
      setCompanyLoading(true);
      const { company, error } = await getCompanyByHashId({
        token,
        hashId: selectedHash as string,
      });

      if (!error) {
        setSelectedCompany(company);
        getCompanyData(company);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCompanyLoading(false);
    }
  }, [selectedHash, token, getCompanyData, isAdvisor]);

  const isFiltering: boolean = useMemo(
    () => !areSameObjects(filter, DEFAULT_V2_USERS_FILTER),
    [filter],
  );

  const resetFilters = (): void => setFilter(DEFAULT_V2_USERS_FILTER);

  const onClientFilterChange = (newFilter: Partial<ClientsFilterObject>) => {
    const newFilters: ClientsFilterObject = {
      ...filter,
      ...newFilter,
    };

    setFilter(newFilters);
  };

  const addOrUpdateUser = (user: User) => {
    if (!user) return;

    const existingUserIndex = users.findIndex((u) => u.id === user.id);

    if (existingUserIndex >= 0) {
      const updatedUsers = [...users];

      updatedUsers[existingUserIndex] = {
        ...updatedUsers[existingUserIndex],
        ...user,
      };

      setUsers([...updatedUsers]);
    } else {
      setUsers((prevUsers) => [user, ...prevUsers]);
    }
  };

  const removeUser = (userId?: number) => {
    if (!userId) return;
    setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
  };

  const onPaginationChange = (callback: any) => {
    setPagination((prev: any) => {
      const nextVals = callback(prev);
      setCookie(USERS_PAGE_SIZE_COOKIE, nextVals?.pageSize?.toString());
      return nextVals;
    });
  };

  const loadSelectedUserCompanies = useCallback(async () => {
    if (typeof selectedUser?.id !== 'number' || !token || !isAdvisor) return;

    try {
      const companies = await getUserCompanies({
        token,
        userId: selectedUser?.id!,
      });

      if (Array.isArray(companies) && !companies?.error) {
        setSelectedUserCompanies(companies);
      }
    } catch (error) {
      console.error('Error loading selected user companies', error);
    }
  }, [token, isAdvisor, selectedUser?.id]);

  const loadEntityTypeCounts = useCallback(async () => {
    if (!token) return;

    try {
      const counts: Record<string, number> = {};

      const promises = firmEntityTypes.map(async (entityType) => {
        const { count } = await getAdminEntities({
          ...DEFAULT_V2_ENTITY_FILTER,
          token,
          rows_per_page: -1,
          entity_type: entityType,
          count: true,
        });
        counts[entityType] = count ?? 0;
      });

      await Promise.all(promises);

      setEntityTypeCounts(counts);
    } catch (error) {
      console.error('Error loading entity type counts:', error);
    }
  }, [token, firmEntityTypes]);

  useEffect(() => {
    // If we are searching, we want to start from the first page
    if (!!filter?.search_term) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [filter?.search_term]);

  useEffect(() => {
    if (!selectedHash) {
      setSelectedCompany(undefined);
      resetCompanyState();

      return;
    }

    loadCompany();
  }, [selectedHash, loadCompany]);

  useEffect(() => {
    const usersPageSizeCookie = getCookie(USERS_PAGE_SIZE_COOKIE);

    if (usersPageSizeCookie) {
      const parsed: number = parseInt(usersPageSizeCookie as string);

      if (parsed && !isNaN(parsed)) {
        setPagination((prev) => ({
          ...prev,
          pageSize: parsed,
        }));
      }
    }
  }, []);

  useEffect(() => {
    loadSelectedUserCompanies();
  }, [loadSelectedUserCompanies, selectedUser?.id]);

  useEffect(() => {
    if (token && isAdvisor) {
      loadEntityTypeCounts();
    }
  }, [token, isAdvisor, loadEntityTypeCounts]);

  return (
    <CompanyContext
      value={{
        addOrUpdateUser,
        allUserIds,
        columns,
        entityTypeCounts,
        filter,
        filteredClients,
        isCompanyLoading,
        isConversationsLoading,
        isFiltering,
        isLoading,
        isQuestionnairesLoading,
        isSelectedCompanySubscribedToNotifications,
        isSignatureRequestsLoading,
        isTaxReturnsLoading,
        loadEntityTypeCounts,
        loadUsersForTable: _loadUsersForTable,
        newClientEntityType,
        onClientFilterChange,
        onToggleAddClient,
        onToggleClientTags,
        onToggleEditUser,
        onToggleImportClients,
        pagination,
        refreshCompanyConversations: loadCompanyConversations,
        refreshCompanyDetails: loadCompanyDetails,
        refreshCompanyQuestionnaires: loadCompanyQuestionnaires,
        refreshCompanySignatureRequests: loadCompanySignatureRequests,
        refreshCompanyTaxReturns: loadCompanyTaxReturns,
        refreshExportableDocs: loadExportableDocs,
        removeUser,
        resetFilters,
        selectedClient,
        selectedCompany,
        selectedCompanyDetails,
        selectedCompanyConversations,
        selectedCompanyInternalNotes,
        selectedCompanyQuestionnaires,
        selectedCompanyRelatedCompanies,
        selectedCompanySignatureRequests,
        selectedCompanyTags,
        selectedCompanyTaxReturns,
        selectedHash,
        selectedTaxReturn,
        selectedUser,
        selectedUserCompanies,
        selectedUserPrimaryCompanies,
        setFilter,
        setIsSorted,
        setNewClientEntityType,
        setPagination: onPaginationChange,
        setSelectedTaxReturn,
        setSelectedUser,
        setShowAddClientsImport,
        setSorting,
        sorting,
        updateSelectedCompany,
        usersCount,
        usersForTable: users,
        dropLinkUpload,
        setDropLinkUpload,
        exportableDocs,
        isExportableDocsLoading,
        hasAnyExportableDocs,
        mostRecentYearWithDocs,
      }}
    >
      <ClientsAddDialog isOpen={showAddClient} onClose={onToggleAddClient} />
      <ClientsImportDialog
        isOpen={showAddClientsImportDialog}
        onClose={onCloseClientImportDialog}
        onToggleDialog={onToggleImportClients}
      />
      <UserEditDialog isOpen={isEditingUser} onClose={onToggleEditUser} />
      <ClientTagsDialog isOpen={showClientTags} onClose={onToggleClientTags} />
      {children}
    </CompanyContext>
  );
};

export { CompanyContext, CompanyProvider };
